import { PartImageIdentification, CropPartImageItemRequest } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PART_IMAGE_IDENTIFICATION_QUERY_KEY } from './useGetPartImageIdentification';

const useStoreIdentification = (identificationId: string) => {
  const queryClient = useQueryClient();
  return (identification: PartImageIdentification) =>
    queryClient.setQueryData([PART_IMAGE_IDENTIFICATION_QUERY_KEY, identificationId], identification);
};

export const useMergePartImageItems = (identificationId: string) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const storeIdentification = useStoreIdentification(identificationId);
  return useMutation({
    mutationFn: async (itemIndices: number[]) => {
      const response = await api.tenants.mergePartImageItems(tenantId, identificationId, { itemIndices });
      return response.data.partImageIdentification;
    },
    onSuccess: storeIdentification,
  });
};

export interface CropPartImageItemVariables extends CropPartImageItemRequest {
  itemIndex: number;
}

export const useCropPartImageItem = (identificationId: string) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const storeIdentification = useStoreIdentification(identificationId);
  return useMutation({
    mutationFn: async ({ itemIndex, ...data }: CropPartImageItemVariables) => {
      const response = await api.tenants.cropPartImageItem(tenantId, identificationId, itemIndex, data);
      return response.data.partImageIdentification;
    },
    onSuccess: storeIdentification,
  });
};

const useItemAction = (identificationId: string, action: 'splitPartImageItem' | 'unmergePartImageItem') => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const storeIdentification = useStoreIdentification(identificationId);
  return useMutation({
    mutationFn: async (itemIndex: number) => {
      const response = await api.tenants[action](tenantId, identificationId, itemIndex);
      return response.data.partImageIdentification;
    },
    onSuccess: storeIdentification,
  });
};

export const useSplitPartImageItem = (identificationId: string) =>
  useItemAction(identificationId, 'splitPartImageItem');

export const useUnmergePartImageItem = (identificationId: string) =>
  useItemAction(identificationId, 'unmergePartImageItem');
